//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { makeStyles, Chip } from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
// import { useParams, useHistory } from 'react-router-dom';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SearchIcon from '@material-ui/icons/Search';
import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_TALENT_CENTER_JOB_TYPE,
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_TALENT_CENTER_SALARY_RATE,
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_TALENT_CENTER_WILLING_TO_RELOCATE,
    ANALYTICS_EVENT_SEARCH,
    ANALYTICS_EVENT_SEARCH_FILTER,
    FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES,
    PAGE_TITLE_TALENT_CENTER,
    PLEASE_CHECK_BACK_VERY_SOON,
    THERE_ARE_NO_JOB_SEEKERS_TO_DISPLAY_AT_THE_MOMENT,
} from '../../common/constants';
import { getUniqueKey } from '../../common/arrayHelper';
import { getMaxSalaryRateFilterText } from '../../common/textHelper';
import {
    // ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT,
    ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC,
    SEARCH_PUBLIC_INITIAL_SORT_OPTIONS,
    SEARCH_WITHOUT_LAT_LONG_SORT_OPTIONS,
} from '../../common/elasticSearchHelper';
import { isQueryEmpty } from '../../common/queryHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { fetchJobSeekerProfiles, fetchSavedJobSeekersByEmployer, updateRecentSearches } from '../../common/services/talentCenterServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPagination from '../../common/elemental/AfitdPagination';
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import DropDown from './DropDown';
// import SearchInput from './SearchInput';
import UserListItem from './UserListItem';
import FiltersBar from './FilterBar';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEqual from 'lodash/isEqual';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import _forEach from 'lodash/forEach';
import _findIndex from 'lodash/findIndex';
import _slice from 'lodash/slice';
import _filter from 'lodash/filter';
import _size from 'lodash/size';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { isDefaultSearch } = params;

    return makeStyles(theme => ({
        root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 20,
        },
        chipBox: {
            padding: '10px 0px',
            display: 'flex',
            flexWrap: 'wrap',
        },
        chip: {
            borderRadius: '5px',
            fontSize: '13px',
            border: '2px solid #1D8A8B',
            background: 'rgba(150,240,241,0.1)',
            margin: '4px',

            '& .MuiSvgIcon-root ': {
                height: '18px !important',
                fill: '#97A19F',
            },
        },
        jobsMainContainer: {
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
        },
        header: {
            display: 'flex',
            padding: '20px',
            alignItems: 'center',
        },
        talentsFound: {
            // textTransform: 'lowercase',
            fontSize: isDefaultSearch ? 20 : 18,
            fontWeight: 'bold',
            lineHeight: '21px',
            letterSpacing: '-1px',
        },
        sortBox: {
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'flex-end',
        },
        sortText: {
            padding: '0 8px',
            [theme.breakpoints.down('sm')]: { paddingLeft: 0 },
        },
        sortDropDown: {
            maxWidth: '130px',
            color: '#000',
            borderRadius: '5px',
            border: '2px solid #000',
            boxShadow: 'none',
            '&:hover': {
                content: 'unset',
            },
            '&:after, &:before': {
                content: 'unset',
            },
        },
        searchItems: {
            flex: 1,
            // maxHeight: '65vh',
            // overflowY: 'scroll',
            // '&::-webkit-scrollbar': {
            //     width: '0.4em',
            // },
            // '&::-webkit-scrollbar-track': {
            //     '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
            // },
            // '&::-webkit-scrollbar-thumb': {
            //     backgroundColor: 'rgba(0,0,0,.1)',
            //     borderRadius: '5px',
            // },
            overflowY: 'hidden',
            paddingBottom: 20,
        },
        clearAllFiltersContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            [theme.breakpoints.only('sm')]: { marginRight: 16 },
        },
        clearAllFiltersText: {
            color: 'blue',
            cursor: 'pointer',
        },
        hidden: {
            display: 'none',
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: 8,
        },
        searchFilterContainer: {
            [theme.breakpoints.only('lg')]: { display: 'flex' },
            [theme.breakpoints.down('sm')]: { padding: '0px 15px' },
        },
        filterSmContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            background: '#F0F2F6',
            borderRadius: 4,
            marginTop: 10,
        },
        filterSmItem1: {
            paddingLeft: 16,
        },
        filterSmItem2: {
            paddingRight: 19,
            display: 'flex',
            alignItems: 'center',
        },
        noResultIcon: {
            fontSize: 180,
            color: '#808080',
        },
        noResultContainer: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
        },
        noResultText: {
            fontSize: 20,
            fontWeight: 'bold',
        },
        noResultTryText: {
            fontSize: 16,
            color: '#6F6D7A',
        },
        noResultTextContainer: {
            textAlign: 'center',
            marginTop: '-20px',
        },
        preSearchDiv: {
            paddingTop: '20px',
            // marginTop: '150px',
            // [theme.breakpoints.down('lg')]: {
            //     marginTop: '0px',
            // },
        },
        perSearchText: {
            color: 'grey',
            textAlign: 'center',
            marginTop: 10,
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        isDefaultSearch,
        // sortTag,
        setSortTag,
        // selected,
        handleClearFilters,
        clearAllFiltersContainerIsHidden,
        currentPageNumber,
        handleChangePage,
        searchResultTotal,
        // setSearchResultTotal,
    } = params;
    const { query, setQuery } = props;
    const { clearAllFiltersContainer: clearAllFiltersContainerCls, hidden: hiddenCls } = classes;

    const paginationCount = Math.ceil(searchResultTotal / 10);

    const talentsFoundText = isDefaultSearch ? 'Talent Center' : `${searchResultTotal} ${searchResultTotal === 1 ? 'Job seeker Found' : 'Job seekers Found'}`;

    const defns = {
        search: {
            query,
            setQuery,
        },
        sortDropDown: {
            title: 'Date',
            // options: SORT_OPTIONS,
            options: getSortDropDownOptions({ isDefaultSearch }),
            onChange: val => setSortTag(val),
        },
        clearAllFiltersContainer: {
            className: cx(clearAllFiltersContainerCls, { [hiddenCls]: clearAllFiltersContainerIsHidden }),
        },
        clearAllFiltersText: {
            onClick: handleClearFilters,
        },
        chip: {
            deleteIcon: <CloseOutlined />,
        },
        sortText: {
            variant: 'body1',
        },
        filterBar: {
            query,
            setQuery,
        },
        talentSearchPagination: {
            count: paginationCount,
            color: 'secondary',
            page: currentPageNumber,
            onChange: handleChangePage,
        },
        perSearchText: {
            variant: 'body2',
        },
        talentsFound: {
            value: talentsFoundText,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getSortDropDownOptions = ({ isDefaultSearch }) => {
    return isDefaultSearch ? SEARCH_PUBLIC_INITIAL_SORT_OPTIONS : SEARCH_WITHOUT_LAT_LONG_SORT_OPTIONS;
};

// Filter chip list
const FilterChipList = props => {
    const classes = useStyles()();
    const chipList = [];
    const { query, onDelete } = props;

    // willing to relocate
    const willingRelocate = query?.willingRelocate || false;
    willingRelocate && chipList.push({ name: 'willingRelocate', value: 'Willing to Relocate' });

    // job types
    const jobTypeList = query?.jobType || [];
    _forEach(jobTypeList, item => {
        item !== FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES && chipList.push({ name: 'jobType', value: item });
    });

    // date posted
    const datePost = query?.datePost || null;
    datePost && chipList.push({ name: 'datePost', value: datePost });

    // salary/rate
    const salaryRate = query?.salaryRate || null;
    if (salaryRate) {
        const rateMax = salaryRate.rateMax;
        const payFrequency = salaryRate.payFrequency;
        const currencySymbol = salaryRate.currencySymbol;

        if (!_isNil(rateMax) && !_isNil(payFrequency)) {
            chipList.push({ name: 'salaryRate', value: getMaxSalaryRateFilterText(rateMax, currencySymbol, payFrequency) });
        }
    }

    // definition
    const defns = {
        chip: {
            deleteIcon: <CloseOutlined />,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    // render
    return chipList.map(item => (
        <div key={getUniqueKey()}>
            <Chip
                label={item.value}
                onDelete={() => {
                    onDelete && onDelete(item.name, item.value);
                }}
                {...defns.chip}
            />
        </div>
    ));
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SideBar = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { userId, employerId, esToken, setEsToken } = appContext;

    const layoutContext = useContext(LayoutContext);
    const {
        queryInitState,
        recentSearches,
        requestRefreshSavedToJobs,
        setRequestRefreshSavedToJobs,
        searchResultTotal = 0,
        setSearchResultTotal,
        setAsyncCallCount,
    } = layoutContext;
    const theme = useTheme();
    const xlMatches = useMediaQuery(theme.breakpoints.up('xl'));
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const lastQuery = useRef();
    const lastQueryPageNumber = useRef();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
    const [talents, setTalents] = useState([]);
    // const [searchResultTotal, setSearchResultTotal] = useState(0);
    const [hideResults, setHideResults] = React.useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    // const [selectedTalentUserId, setSelectedTalentUserId] = useState();

    // Sorting is removed from UI per requirement -el 20211210
    // Keep the state in case we need sorting in future.  For now, always sort by Date -el 20211210
    // const [sortTag, setSortTag] = useState('Date');
    const [sortTag, setSortTag] = useState(ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC);
    const [noResultIcon] = useState(false);
    const myRef = useRef(null);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onDeleteChip, setSelected, selected, query, setQuery, onClickItem, showFilterPage, setShowFilterPage, searchedPerformed, setSearchPerformed, setShowAds } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isDefaultSearch = _isEqual(_omit(query, ['sortTag']), _omit(queryInitState, ['sortTag']));

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // ElasticSearch
    //---------------------------------------------------------------------
    const searchTalents = useCallback(
        async ({ page = 1, forceQuery, isHideResultCount = false } = {}) => {
            // search the talents (job seeker profiles)
            setAsyncCallCount && setAsyncCallCount(prev => prev + 1);
            const searchResult = await fetchJobSeekerProfiles({
                queryObject: forceQuery || query,
                from: page,
                esToken,
                setEsToken,
            });
            setSearchPerformed(true);
            setHideResults(false);
            setSearchResultTotal(_get(searchResult, 'total', 0));
            const talentList = _get(searchResult, 'list', []);

            // for this result set, get the list of employer-jobs the talents have been added to.
            const jsIdList = _map(talentList, item => item.uid);
            if (_size(jsIdList) > 0) {
                const savedJobSeekers = await fetchSavedJobSeekersByEmployer({ jsIdList, employerId });

                _forEach(savedJobSeekers, savedJs => {
                    const idx = _findIndex(talentList, item => item.uid === savedJs.jsId);
                    if (!_get(talentList, `[${idx}].savedToJobs`)) _set(talentList, `[${idx}].savedToJobs`, []); // initialize array
                    _get(talentList, `[${idx}].savedToJobs`).push(savedJs);
                });
            }

            // update state
            setTalents(talentList);

            // log events
            const searchTerm = query?.occupation || '_BLANK_';
            logEvent(ANALYTICS_EVENT_SEARCH, { search_term: searchTerm, page_title: PAGE_TITLE_TALENT_CENTER });

            _size(query?.jobType) > 0 && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_TALENT_CENTER_JOB_TYPE });
            query?.salaryRate && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_TALENT_CENTER_SALARY_RATE });
            query?.willingRelocate && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_TALENT_CENTER_WILLING_TO_RELOCATE });

            // if (isSelectFirstTalent && talentList.length > 0) {
            //     const firstTalent = jobList[0];
            //     setSelectedTalent(firstTalent);
            //     setSelectedTalentUserId(firstTalent.uid);
            // }
            // // myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

            setAsyncCallCount && setTimeout(() => setAsyncCallCount(prev => prev - 1), 500);
        },
        [query, esToken, setEsToken, setSearchPerformed, employerId, setSearchResultTotal, setAsyncCallCount],
    );

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // when query or page number has changed, execute search
    useEffect(() => {
        // console.log({ query });
        //TODO Remove this condition when we are not loading all jobs on page load
        if (initialLoad) {
            searchTalents({ page: 1, isSelectFirstJob: false, forceQuery: queryInitState, isHideResultCount: true });
            setInitialLoad(false);

            // remember the query and the page number
            lastQuery.current = _cloneDeep(query);
            lastQueryPageNumber.current = currentPageNumber;
            ////
            //TODO Uncomment this when we are not loading all jobs on page load
            // } else if (!_isEqual(query, queryInitState)) {
        } else {
            if (query && currentPageNumber) {
                const sameQuery = _isEqual(lastQuery.current, query);
                const samePageNumber = lastQueryPageNumber.current === currentPageNumber;
                const singleTalentQuery = query?.id;

                // if query is same as last, do not search
                if (lastQuery && lastQueryPageNumber && (!sameQuery || !samePageNumber)) {
                    console.log('############################### Talent Center searching (query changed)', { query });

                    searchTalents({ page: currentPageNumber, isSelectFirstJob: singleTalentQuery }); //TODO When re-running search, should we keep selected talent on the right pane?

                    // update the Recent Searches
                    if (!isQueryEmpty(query) && !sameQuery && !singleTalentQuery) {
                        const prevSearches = [...recentSearches];
                        if (prevSearches.length >= 5) prevSearches.shift();
                        updateRecentSearches({ userId, recentSearches: [...prevSearches, query] });
                    }

                    // remember the query and the page number
                    lastQuery.current = _cloneDeep(query);
                    lastQueryPageNumber.current = currentPageNumber;
                }
            }
        }
    }, [initialLoad, query, currentPageNumber, searchTalents, recentSearches, lastQuery, lastQueryPageNumber, userId, queryInitState]);

    // //filter by title
    // useEffect(() => {
    //     if (query.jobTitle !== null) {
    //         console.log({ jobTitle: query.jobTitle });
    //         const fe = userProfiles.filter(element => element.data?.occupation?.includes(query.jobTitle));
    //         const temp = [];
    //         filteredUsers.forEach(element => {
    //             fe.forEach(filteredElement => {
    //                 if (element.id === filteredElement.id) {
    //                     temp.push(element);
    //                 }
    //             });
    //         });
    //         console.log({ fe, temp });
    //         if (temp.length > 0) {
    //             console.log('setting', { temp });
    //             setFilteredUsers(temp);
    //         }
    //     } else {
    //         console.log('in else');
    //         setFilteredUsers(allUsers);
    //     }
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [query.jobTitle]);
    // // console.log({ filteredUsers });

    // //filter by Location
    // useEffect(() => {
    //     if (query.location !== null) {
    //         console.log({ location: query.location });
    //         const fe = userProfiles.filter(element => element.data?.state?.includes(query.location));
    //         const temp = [];
    //         filteredUsers.forEach(element => {
    //             fe.forEach(filteredElement => {
    //                 if (element.id === filteredElement.id) {
    //                     temp.push(element);
    //                 }
    //             });
    //         });
    //         if (temp.length > 0) {
    //             setFilteredUsers(temp);
    //         }
    //     } else {
    //         console.log('in else');
    //         setFilteredUsers(allUsers);
    //     }
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [query.location]);

    // refresh savedToMyJobs
    useEffect(() => {
        if (requestRefreshSavedToJobs) {
            const { jobSeekerId, newSavedToJobs } = requestRefreshSavedToJobs;
            setTalents(prev => {
                const idx = _findIndex(talents, talent => talent.uid === jobSeekerId);
                const foundTalent = talents[idx];

                // remove the jobs that are marked 'isDelete' and for the remaining ones, remove the 'isNew' field
                const savedToJobs = _map(
                    _filter(newSavedToJobs, job => !job.isDelete),
                    job => _omit(job, ['isNew']),
                );

                const talentDto = { ...foundTalent, savedToJobs };

                return [..._slice(prev, 0, idx), talentDto, ..._slice(prev, idx + 1)];
            });

            // reset
            setRequestRefreshSavedToJobs(null);
        }
    }, [talents, requestRefreshSavedToJobs, setRequestRefreshSavedToJobs]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangePage = (event, value) => {
        setCurrentPageNumber(value);
    };

    const handleClearFilters = () => {
        // console.log('removing filters');
        setQuery(queryInitState);

        //TODO Change this back to ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT when we are not loading all jobs on page load
        setSortTag(ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC);
        // setSortTag(ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT);

        setSearchPerformed(false);
        setHideResults(true);
        // Make Ads Display Again.....
        setShowAds(true);
        // Make Ads Display Again.....

        //TODO Remove this when we are not loading all jobs on page load
        searchTalents({ page: 1, isSelectFirstJob: false, isHideResultCount: true, forceQuery: queryInitState });
    };

    const handleDeleteChip = (name, value) => {
        onDeleteChip && onDeleteChip(name, value);
    };

    //---------------------------------------------------------------------
    // Misc
    //---------------------------------------------------------------------
    const clearAllFiltersContainerIsHidden = _isEqual(query, queryInitState);
    // salaryFilter === FILTER_OPTION_SALARY_ANY_SALARY &&
    // rateFilter === FILTER_OPTION_RATE_ANY_RATE &&
    // payFrequencyFilter === FILTER_OPTION_PAY_FREQUENCY_ANY_FREQUENCY;

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isDefaultSearch })();
    const defns = getDefns({
        classes,
        props,
        isDefaultSearch,
        queryInitState,
        sortTag,
        setSortTag,
        selected,
        handleClearFilters,
        clearAllFiltersContainerIsHidden,
        currentPageNumber,
        handleChangePage,
        searchResultTotal,
        setSearchResultTotal,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            {showFilterPage && smMatches ? (
                <FiltersBar {...defns.filterBar} setShowFilterPage={setShowFilterPage} setShowAds={setShowAds} />
            ) : (
                <div {...defns.root}>
                    <div {...defns.searchFilterContainer}>
                        {/* <SearchInput {...defns.search} /> */}
                        {lgMatches ? (
                            smMatches ? (
                                <div {...defns.filterSmContainer} onClick={() => setShowFilterPage(true)}>
                                    <div {...defns.filterSmItem1}>
                                        <p style={{ fontSize: '15px' }}>Filter</p>
                                    </div>
                                    <div {...defns.filterSmItem2}>
                                        <KeyboardArrowDownIcon />
                                    </div>
                                </div>
                            ) : null
                        ) : // <FiltersBar {...defns.filterBar} />

                        null}
                    </div>

                    <div {...defns.chipBox}>
                        <FilterChipList query={query} onDelete={handleDeleteChip} />
                    </div>
                    <div {...defns.clearAllFiltersContainer}>
                        <AfitdTypography {...defns.clearAllFiltersText}>Clear all filters</AfitdTypography>
                    </div>

                    <div {...defns.jobsMainContainer}>
                        {!searchedPerformed ? (
                            <div {...defns.preSaveHeader}>
                                <div {...defns.preSearchDiv}>
                                    <AfitdTypography variant="h6" align="center">
                                        <b>Talent Center </b>
                                    </AfitdTypography>
                                    <AfitdTypography {...defns.perSearchText}>Please use the search box or filters to perform a search</AfitdTypography>
                                </div>
                            </div>
                        ) : lgMatches && isDefaultSearch && _size(talents) === 0 ? (
                            <div {...defns.preSaveHeader}>
                                <div {...defns.preSearchDiv}>
                                    <AfitdTypography variant="h6" align="center">
                                        <b>{THERE_ARE_NO_JOB_SEEKERS_TO_DISPLAY_AT_THE_MOMENT}</b>
                                    </AfitdTypography>
                                    <AfitdTypography {...defns.perSearchText}>{PLEASE_CHECK_BACK_VERY_SOON}</AfitdTypography>
                                </div>
                            </div>
                        ) : (
                            <div {...defns.header}>
                                <div>
                                    <AfitdTypography {...defns.talentsFound} />
                                </div>
                                {/* <div {...defns.sortBox}>
                                <AfitdTypography {...defns.sortText}>{smMatches ? null : 'Sort by:'}</AfitdTypography>
                                <DropDown {...defns.sortDropDown} />
                            </div> */}
                            </div>
                        )}
                        <div {...defns.searchItems}>
                            <SimpleBar style={{ height: '100%' }}>
                                <div ref={myRef} />
                                {/* {filteredUsers.length > 0 && (
                                    <div {...defns.sortBox}>
                                        <AfitdTypography {...defns.sortText}>Sort by:</AfitdTypography>
                                        <DropDown {...defns.sortDropDown} />
                                    </div>
                                )} */}

                                {!hideResults &&
                                    _map(talents, talent => {
                                        const userId = talent.uid;
                                        const data = _omit(talent, 'uid');
                                        return xlMatches ? (
                                            <UserListItem
                                                {...data}
                                                userId={userId}
                                                selected={selected?.id === userId}
                                                setSelected={() => {
                                                    setSelected({ id: userId, data });
                                                    onClickItem();
                                                }}
                                            />
                                        ) : (
                                            <div key={getUniqueKey()}>
                                                <UserListItem
                                                    {...data}
                                                    userId={userId}
                                                    selected={selected?.id === userId}
                                                    setSelected={() => {
                                                        setSelected({ id: userId, data });
                                                        onClickItem();
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}

                                {noResultIcon && searchResultTotal === 0 ? (
                                    <div {...defns.noResultContainer}>
                                        <SearchIcon {...defns.noResultIcon} />
                                        <div {...defns.noResultTextContainer}>
                                            <p {...defns.noResultText}>There are no job seekers that match the criteria</p>
                                            <p>Please change filter choices and try again</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        {...defns.paginationContainer}
                                        onClick={() => {
                                            setTimeout(() => {
                                                myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }, 100);
                                        }}
                                    >
                                        {searchedPerformed && <AfitdPagination {...defns.talentSearchPagination} />}
                                    </div>
                                )}
                            </SimpleBar>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default React.memo(SideBar);
